import * as z from "@-/com.npmjs.zod";
import { uuidv4 } from "@-/com.npmjs.uuid";

const customCardSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("card"),
  morpheme: z.string().trim().min(1),
  part: z.enum(["meaning", "meaning-and-pronunciation", "pronunciation", "word"]),
});
const customSlideSchema = z.object({
  body: z.string(),
  id: z.string().min(1),
  kind: z.literal("slide"),
  title: z.string(),
});
const customLevelSchema = z.object({
  description: z.string(),
  id: z.string().min(1),
  items: z.array(z.discriminatedUnion("kind", [customCardSchema, customSlideSchema])),
  name: z.string().trim().min(1),
});
export const customLevelPackSchema = z
  .object({
    description: z.string(),
    id: z.string().min(1),
    levels: z.array(customLevelSchema),
    name: z.string().trim().min(1),
    script: z.enum(["simplified", "traditional"]),
  })
  .superRefine((pack, ctx) => {
    const ids = new Set<string>([pack.id]);
    for (const level of pack.levels) {
      for (const id of [level.id, ...level.items.map((item) => item.id)]) {
        if (ids.has(id)) {
          ctx.addIssue({ code: "custom", message: "Every level and item must have a unique id." });
        }
        ids.add(id);
      }
    }
  });

/** Ordered cards and inline slides; all authoring data lives in one synced JSON column. */
export const customLevelPacksSchema = z
  .object({
    packs: z.array(customLevelPackSchema),
    version: z.literal(1),
  })
  .superRefine(({ packs }, ctx) => {
    const ids = new Set<string>();
    for (const pack of packs) {
      if (ids.has(pack.id)) {
        ctx.addIssue({ code: "custom", message: "Every pack must have a unique id." });
      }
      ids.add(pack.id);
    }
  });
export const customLevelPackExportSchema = z.object({
  format: z.literal("haniverse.level-pack"),
  pack: customLevelPackSchema,
  version: z.literal(1),
});
export type CustomCard = z.infer<typeof customCardSchema>;
export type CustomSlide = z.infer<typeof customSlideSchema>;
export type CustomLevelItem = CustomCard | CustomSlide;
export type CustomLevel = z.infer<typeof customLevelSchema>;
export type CustomLevelPack = z.infer<typeof customLevelPackSchema>;
export type CustomLevelPacks = z.infer<typeof customLevelPacksSchema>;

export const EMPTY_CUSTOM_LEVEL_PACKS: CustomLevelPacks = { packs: [], version: 1 };

export function createCustomCard(
  morpheme: string,
  part: CustomCard["part"] = "meaning-and-pronunciation",
): CustomCard {
  return { id: uuidv4(), kind: "card", morpheme: morpheme.trim(), part };
}
export function createCustomLevel(name: string): CustomLevel {
  return { description: "", id: uuidv4(), items: [], name };
}
export function createCustomLevelPack(script: CustomLevelPack["script"]): CustomLevelPack {
  return { description: "", id: uuidv4(), levels: [], name: "Untitled pack", script };
}

/** Copies stay independent, even when a source level contains repeated cards or slides. */
export function cloneCustomLevel(level: CustomLevel): CustomLevel {
  return { ...level, id: uuidv4(), items: level.items.map((item) => ({ ...item, id: uuidv4() })) };
}

export function insertCustomSlide(
  level: CustomLevel,
  cardId: string,
  side: "before" | "after",
): { level: CustomLevel; slide: CustomSlide } {
  const index = level.items.findIndex((item) => item.id === cardId && item.kind === "card");
  if (index === -1) {
    throw new Error("Select a card before adding a slide.");
  }
  const slide: CustomSlide = { body: "", id: uuidv4(), kind: "slide", title: "New slide" };
  const items = [...level.items];
  items.splice(index + (side === "after" ? 1 : 0), 0, slide);
  return { level: { ...level, items }, slide };
}

/** Merge one pack into the latest document; reject edits to the same pack from another session. */
export function saveCustomLevelPack(
  document: CustomLevelPacks,
  pack: CustomLevelPack,
  original: CustomLevelPack | null,
): CustomLevelPacks {
  const current = document.packs.find((entry) => entry.id === pack.id) ?? null;
  if (JSON.stringify(current) !== JSON.stringify(original)) {
    throw new Error(
      "This pack changed in another session. Cancel to load the latest version before editing again.",
    );
  }
  const next =
    current === null
      ? [...document.packs, pack]
      : document.packs.map((entry) => (entry.id === pack.id ? pack : entry));
  return customLevelPacksSchema.parse({ packs: next, version: 1 });
}
